import React, { useState } from 'react';
import { X, Calendar, CheckCircle2, ChevronRight, ChevronDown, Sparkles, BookOpen, Clock, AlertCircle } from 'lucide-react';
import { StudyPlan, studyPlanService, PlanTask } from '../services/studyPlanService';
import T from './T';

interface StudyPlanRoadmapModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: StudyPlan;
}

export const StudyPlanRoadmapModal: React.FC<StudyPlanRoadmapModalProps> = ({
  isOpen,
  onClose,
  plan
}) => {
  const [expandedDay, setExpandedDay] = useState<number | null>(plan?.current_day_index || 1);

  if (!isOpen) return null;

  // Gerar amostra dos próximos 30 dias para visualização estruturada
  const previewDays = Array.from({ length: 30 }, (_, i) => i + 1);

  const toggleDay = (dayNum: number) => {
    setExpandedDay(prev => prev === dayNum ? null : dayNum);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/30 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="bg-surface w-full max-w-3xl rounded-[28px] p-6 sm:p-8 border border-black/[0.06] dark:border-white/[0.06] flex flex-col max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-black/[0.06] dark:border-white/[0.06] shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 text-primary rounded-2xl">
              <Calendar size={22} />
            </div>
            <div>
              <h3 className="text-lg font-medium dark:text-ink tracking-[-0.03em]"><T k="ui.plan.detailedRoadmap" /></h3>
              <p className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
                IDIOMA: {plan.language_code.toUpperCase()} • NÍVEL: {plan.current_level} • {plan.daily_goal_minutes} MIN/DIA
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2.5 text-muted hover:text-muted rounded-full transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Dynamic Disclaimer Banner */}
        <div className="my-4 p-4 rounded-2xl bg-warn/10 border border-warn/20 text-warn dark:text-warn flex items-start gap-3 shrink-0">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <p className="text-xs font-bold leading-relaxed">
            Este roteiro é dinâmico e pode ser alterado e reajustado automaticamente de acordo com a sua necessidade de aprendizado, desempenho em exercícios e revisão de erros.
          </p>
        </div>

        {/* Days List Accordion */}
        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3">
          {previewDays.map(dayNum => {
            const isCurrentDay = dayNum === plan.current_day_index;
            const isPastDay = dayNum < plan.current_day_index;
            const isExpanded = expandedDay === dayNum;
            const tasks: PlanTask[] = studyPlanService.generateTasksForDay({
              dayIndex: dayNum,
              level: plan.current_level,
              langCode: plan.language_code,
              dailyMinutes: plan.daily_goal_minutes
            });

            return (
              <div 
                key={dayNum}
                className={`rounded-2xl border transition-all overflow-hidden ${
                  isCurrentDay
                    ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/20'
                    : isPastDay
                    ? 'border-black/[0.06] bg-tint-stone/40 opacity-80'
                    : 'border-black/[0.06] dark:border-white/[0.06] bg-surface '
                }`}
              >
                {/* Accordion Header */}
                <div 
                  onClick={() => toggleDay(dayNum)}
                  className="p-4 flex items-center justify-between cursor-pointer select-none hover:bg-tint-stone/50 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-normal uppercase font-mono tracking-[0.14em] ${
                      isCurrentDay 
                        ? 'bg-ink text-surface' 
                        : isPastDay
                        ? 'bg-ok/10 text-ok'
                        : 'bg-tint-stone text-ink '
                    }`}>
                      {isCurrentDay ? 'HOJE • DIA ' + dayNum : isPastDay ? 'CONCLUÍDO • DIA ' + dayNum : 'DIA ' + dayNum}
                    </span>
                    <span className="text-xs text-muted font-bold">• {plan.daily_goal_minutes} min previstos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPastDay && <CheckCircle2 size={18} className="text-ok" />}
                    <div className="text-muted transition-transform duration-300 ease-in-out">
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                  </div>
                </div>

                {/* Subtasks Accordion Content */}
                <div 
                  className={`grid transition-all duration-300 ease-in-out ${
                    isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="p-4 pt-1 border-t border-black/[0.06]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {tasks.map((task, tIdx) => (
                          <div 
                            key={tIdx}
                            className="p-2.5 rounded-xl bg-surface border border-black/[0.06] flex items-center justify-between text-xs"
                          >
                            <div className="flex items-center gap-2 truncate pr-2">
                              <span className="text-primary font-semibold">•</span>
                              <span className="font-bold text-ink truncate">{task.title}</span>
                            </div>
                            <span className="text-[10px] font-semibold text-muted shrink-0">{task.estimated_minutes}m</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-black/[0.06] dark:border-white/[0.06] flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:scale-[1.02] active:scale-95 transition-all cursor-pointer"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};
